import React, { useEffect } from 'react';
import { ThemeProvider } from '@material-ui/core/styles';
import { CssBaseline } from '@material-ui/core';
import { useAuthState } from 'react-firebase-hooks/auth';
import { jwtDecode } from 'jwt-decode';
import theme from './styles/theme';
import { StateProvider, useStateValue } from './context/state/provider';
import { auth, getUserOnLoad } from './services/firebase/firebase';
import { VIEW } from './constants/view.constants';
import WizardContainer from './components/WizardContainer';
import SuccessView from './components/SuccessView';
import ErrorView from './components/ErrorView';

function AppContent() {
    const [user, loading] = useAuthState(auth);
    const [{ view }, dispatch] = useStateValue();

    // Parse JWT token for secure admin panel integration
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('token');
        
        if (token) {
            try {
                // Decode JWT token (without verification on frontend for now)
                // Note: Full verification happens on backend API calls
                const decoded = jwtDecode(token);
                
                if (decoded && decoded.email && decoded.clientId) {
                    console.log('🔐 Secure admin panel integration detected:', { 
                        email: decoded.email, 
                        clientId: decoded.clientId, 
                        currentAgents: decoded.currentAgents || 0,
                        expiresAt: new Date(decoded.exp * 1000).toISOString()
                    });
                    
                    // Check if token is expired
                    const now = Math.floor(Date.now() / 1000);
                    if (decoded.exp && decoded.exp < now) {
                        console.error('🚨 JWT token has expired');
                        dispatch({
                            type: 'SET_ERROR',
                            error: 'Wizard link has expired. Please generate a new link from the admin panel.'
                        });
                        dispatch({
                            type: 'SET_VIEW',
                            view: VIEW.ERROR
                        });
                        return;
                    }
                    
                    // Store token for API calls
                    sessionStorage.setItem('wizardToken', token);
                    
                    dispatch({
                        type: 'UPDATE_WIZARD_DATA',
                        data: {
                            userDetails: { 
                                email: decoded.email,
                                firstName: '',
                                lastName: '',
                                company: ''
                            },
                            clientId: decoded.clientId,
                            prefilledFromAdmin: true,
                            currentAgents: decoded.currentAgents || 0,
                            wizardToken: token // Store token for backend verification
                        }
                    });
                    
                    // Set admin panel user flag for email verification bypass
                    dispatch({
                        type: 'UPDATE_EMAIL_VERIFICATION',
                        data: {
                            isAdminPanelUser: true,
                            isVerified: true // Skip email verification for admin panel users
                        }
                    });
                } else {
                    console.error('🚨 Invalid JWT token structure');
                    dispatch({
                        type: 'SET_ERROR',
                        error: 'Invalid wizard link. Please generate a new link from the admin panel.'
                    });
                    dispatch({
                        type: 'SET_VIEW',
                        view: VIEW.ERROR
                    });
                }
            } catch (error) {
                console.error('🚨 JWT token parsing failed:', error);
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Invalid wizard link format. Please generate a new link from the admin panel.'
                });
                dispatch({
                    type: 'SET_VIEW',
                    view: VIEW.ERROR
                });
            }
        }
    }, [dispatch]);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                if (!user && !loading) {
                    const anonymousUser = await getUserOnLoad();
                    dispatch({
                        type: 'SET_USER',
                        user: anonymousUser
                    });
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
                dispatch({
                    type: 'SET_ERROR',
                    error: 'Failed to initialize authentication'
                });
            }
        };

        initializeAuth();
    }, [user, loading, dispatch]);

    const renderView = () => {
        switch (view) {
            case VIEW.SUCCESS:
                return <SuccessView />;
            case VIEW.ERROR:
                return <ErrorView />;
            case VIEW.WIZARD:
            default:
                return <WizardContainer />;
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            {renderView()}
        </ThemeProvider>
    );
}

function App() {
    return (
        <StateProvider>
            <AppContent />
        </StateProvider>
    );
}

export default App;
